import { Check, X } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/cn";

import { useTimelineLog } from "../api/use-timeline-log";
import { relativeTime } from "../lib/relative-time";
import { STATUS_FLOW } from "../lib/status";
import type { Order } from "../lib/types";

interface StepProps {
  cls: "done" | "current" | "todo" | "cancel";
  title: string;
  note: string;
  index: number;
  isLast: boolean;
}

function TimelineStep({ cls, title, note, index, isLast }: StepProps): JSX.Element {
  const nodeClass = cn(
    "relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-2",
    cls === "done" && "bg-[var(--intent-success-fg)] text-white ring-[var(--intent-success-fg)]",
    cls === "current" && "bg-[var(--intent-info-fg)] text-white ring-[var(--intent-info-fg)]",
    cls === "todo" &&
      "bg-[var(--surface-sunken)] text-[var(--text-subtle)] ring-[var(--premium-border)]",
    cls === "cancel" && "bg-[var(--intent-danger-fg)] text-white ring-[var(--intent-danger-fg)]",
  );

  const icon =
    cls === "done" || cls === "current" ? (
      <Check className="h-3 w-3" />
    ) : cls === "cancel" ? (
      <X className="h-3 w-3" />
    ) : (
      <span className="font-mono text-[10px] font-bold">{index + 1}</span>
    );

  return (
    <div className="flex gap-3" data-testid={`tl-step-${cls}`}>
      <div className="flex flex-col items-center">
        <div className={nodeClass}>{icon}</div>
        {!isLast && <div className="mt-1 w-px flex-1 bg-[var(--premium-border)]" aria-hidden />}
      </div>
      <div className="pb-5">
        <div
          className={cn(
            "text-sm font-semibold",
            cls === "todo"
              ? "text-[var(--text-subtle)]"
              : cls === "cancel"
                ? "text-[var(--intent-danger-fg)]"
                : "text-[var(--text-primary)]",
          )}
        >
          {title}
        </div>
        <div className="mt-0.5 whitespace-pre-line text-xs text-[var(--text-subtle)]">{note}</div>
      </div>
    </div>
  );
}

interface TimelineProps {
  order: Order;
}

export function Timeline({ order }: TimelineProps): JSX.Element {
  const { t, i18n } = useTranslation("orders");
  const { data: timelineEntries = [] } = useTimelineLog(order.id);

  if (order.status === "cancelled") {
    const cancelledSteps: Array<{ key: string; cls: "done" | "cancel" }> = [
      { key: "received", cls: "done" },
      { key: "reserved", cls: "done" },
      { key: "cancelled", cls: "cancel" },
    ];
    return (
      <div className="flex flex-col">
        {cancelledSteps.map((s, i) => (
          <TimelineStep
            key={s.key}
            cls={s.cls}
            title={s.cls === "cancel" ? t("st_cancelled") : t(`st_${s.key}`)}
            note={s.cls === "cancel" ? t("tl_cancelled") : t(`tl_${s.key}`)}
            index={i}
            isLast={i === cancelledSteps.length - 1}
          />
        ))}
      </div>
    );
  }

  if (order.status === "superseded") {
    const supersededSteps: Array<{ key: string; cls: "done" | "cancel" }> = [
      { key: "received", cls: "done" },
      { key: "superseded", cls: "cancel" },
    ];
    return (
      <div className="flex flex-col" data-testid="timeline-superseded">
        {supersededSteps.map((s, i) => (
          <TimelineStep
            key={s.key}
            cls={s.cls}
            title={t(`st_${s.key}`)}
            note={t(`tl_${s.key}`)}
            index={i}
            isLast={i === supersededSteps.length - 1}
          />
        ))}
      </div>
    );
  }

  let reachedIdx = STATUS_FLOW.indexOf(order.status);
  if (order.status === "returned") {
    reachedIdx = STATUS_FLOW.length - 1;
  }

  return (
    <div className="flex flex-col">
      {STATUS_FLOW.map((key, i) => {
        const cls: "done" | "current" | "todo" =
          i < reachedIdx ? "done" : i === reachedIdx ? "current" : "todo";
        const baseNote =
          key === "shipped" && order.tracking
            ? t("tl_shipped_awb", { awb: order.tracking })
            : t(`tl_${key}`);
        // Match real transitions only: the warehouse hand-over row is
        // packed → packed (spec 2026-10-08) and must not steal the Packed
        // step's time. A seed row (fromStatus null) still matches.
        const matchingEntry = timelineEntries.find(
          (e) => e.toStatus === key && e.fromStatus !== e.toStatus,
        );
        const handover =
          key === "packed"
            ? timelineEntries.find((e) => e.channelState === "warehouse_shipped")
            : undefined;
        let note = matchingEntry
          ? `${baseNote} · ${relativeTime(matchingEntry.occurredAt, i18n.language)}`
          : baseNote;
        if (handover) {
          note = `${note}\n${t("tl_packed_handover", {
            time: relativeTime(handover.occurredAt, i18n.language),
          })}`;
        }
        return (
          <TimelineStep
            key={key}
            cls={cls}
            title={t(`st_${key}`)}
            note={note}
            index={i}
            isLast={i === STATUS_FLOW.length - 1}
          />
        );
      })}
    </div>
  );
}
